import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_SEARCH_PRESET,
  DELETE_SEARCH_PRESET,
  GET_SEARCH_PRESETS,
  UPDATE_SEARCH_PRESET,
} from '@gql/talentSearch';
import { useCallback } from 'react';
import { ITalentSearchFilter, PresetVisibility, SearchPreset, SearchPresetFilters } from '../types';

export function useSearchPresets<TFilters extends SearchPresetFilters = ITalentSearchFilter>() {
  const { data, loading, refetch } = useQuery(GET_SEARCH_PRESETS);
  const [createMutation, { loading: creating }] = useMutation(CREATE_SEARCH_PRESET);
  const [updateMutation, { loading: updating }] = useMutation(UPDATE_SEARCH_PRESET);
  const [deleteMutation, { loading: deleting }] = useMutation(DELETE_SEARCH_PRESET);

  const presets: SearchPreset<TFilters>[] = data?.getSearchPresets || [];

  const createPreset = useCallback(
    async (
      name: string,
      filters: TFilters,
      visibility: PresetVisibility,
      description?: string,
    ) => {
      const result = await createMutation({
        variables: {
          input: {
            name,
            description,
            filters: JSON.parse(JSON.stringify(filters)),
            visibility,
          },
        },
        refetchQueries: ['GetSearchPresets'],
      });
      return result.data?.createSearchPreset;
    },
    [createMutation],
  );

  const updatePreset = useCallback(
    async (
      id: string,
      name: string,
      filters: TFilters,
      visibility: PresetVisibility,
      description?: string,
    ) => {
      const result = await updateMutation({
        variables: {
          id,
          input: {
            name,
            description,
            filters: JSON.parse(JSON.stringify(filters)),
            visibility,
          },
        },
        refetchQueries: ['GetSearchPresets'],
      });
      return result.data?.updateSearchPreset;
    },
    [updateMutation],
  );

  const deletePreset = useCallback(
    async (id: string) => {
      await deleteMutation({
        variables: { id },
        refetchQueries: ['GetSearchPresets'],
      });
    },
    [deleteMutation],
  );

  return {
    presets,
    loading,
    creating,
    updating,
    deleting,
    createPreset,
    updatePreset,
    deletePreset,
    refetch,
  };
}
